import { useCallback, useEffect, useState } from "react";
import {
  Col,
  Form,
  InputNumber,
  Radio,
  Row,
  Select,
  Slider,
  Table,
  Tag,
  Tooltip,
  Typography,
  Pagination,
} from "antd";
import { RouteComponentProps } from "react-router";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import "./ArtistsRanking.scss";
import { Card, FieldSelect, Loader } from "shared/components";
import { ReactComponent as ExportIcon } from "shared/assets/images/cloud-download.svg";
import {
  exportArtistRankingCSV,
  exportArtistRankingExcel,
  fetchArtistByName,
  fetchHighlightedArtists,
} from "radar/Service/RadarService";
import { getI18nGenre } from "shared/helpers/i18nGenre";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faExternalLinkAlt,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import { debounce, uniqBy } from "lodash";
import Avatar from "antd/lib/avatar/avatar";
import {
  RADAR_ARTIST_RANKING_FILTER,
  RADAR_ARTIST_RANKING_HIGHLIGHTED_ARTISTS,
} from "shared/constants/LocalStorageConsts";
import moment from "moment";
import RadioButtonRankingOptions from "../radio-button-ranking-options/RadioButtonRankingOptions";
import { ptBRFormat } from "shared/helpers/BRLFormat";
import { UserTypeEnum } from "shared/enums/UserTypeEnum";
import { getCapitalizedLang } from "shared/helpers/capitalized-lang";
import { FileType, downloadFile } from "shared/helpers/download-file";
import {
  ArtistsRankingIndex,
  clearArtistRanking,
  getArtistRanking,
} from "explorer/reducers/rankings";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";

const defaults = {
  defaultForm: {
    country: [],
    genre: [],
    genreSpotify: [],
    stage: [1, 10],
    daysSinceFirstRelease: null,
    daysWithoutRelease: null,
    releasesLastYear: "",
  },
  defaultFilter: {
    country: [],
    genre: [],
    genreSpotify: [],
    stageInit: 1,
    stageEnd: 10,
    offset: 0,
    limit: 10,
  },
};

const initial = () => {
  const savedFilter = localStorage.getItem(RADAR_ARTIST_RANKING_FILTER);
  const highlightedArtists = localStorage.getItem(
    RADAR_ARTIST_RANKING_HIGHLIGHTED_ARTISTS
  );
  let defaultArtists: any[] = [];

  try {
    defaultArtists = JSON.parse(highlightedArtists || "[]").filter(
      (x: any) => x?.artistIdStrm && x?.artistName
    );
  } catch (err) {
    console.error(
      "Erro ao tentar fazer o parse de RADAR_ARTIST_RANKING_HIGHLIGHTED_ARTISTS",
      err
    );
  }

  if (savedFilter) {
    const parsed = JSON.parse(savedFilter);

    const { stageInit = 1, stageEnd = 10, ...rest } = parsed;
    return {
      defaultForm: {
        ...rest,
        stage: [stageInit, stageEnd],
      },
      defaultFilter: parsed,
      defaultArtists,
    };
  }

  return {
    ...defaults,
    defaultArtists,
  };
};

const ArtistsRanking: React.FC<RouteComponentProps> = (props) => {
  const savedIndex = localStorage.getItem(
    "RADAR_RANKING_INDEX"
  ) as ArtistsRankingIndex;
  const { history } = props;
  const { defaultFilter, defaultForm, defaultArtists } = initial();
  const { t, i18n } = useTranslation("radar");
  const [loadingLocal, setLoadingLocal] = useState<boolean>(false);
  const [loadingArtist, setLoadingArtist] = useState<boolean>(false);
  const [enableExport, setEnableExport] = useState<boolean>(false);
  const [loadingHighlightedArtists, setLoadingHighlightedArtists] =
    useState<boolean>(false);
  const [artistsOptions, setArtistsOptions] = useState<any>(
    defaultArtists.map(({ artistIdStrm, artistName, imagesSpotify }: any) => ({
      id: artistIdStrm,
      name: artistName,
      image: (imagesSpotify && imagesSpotify[2]?.url) || "",
    }))
  );
  const [searchedArtists, setSearchedArtists] = useState<any>([]);
  const [selectedArtists, setSelectedArtists] = useState<any>(
    defaultArtists.map(({ artistIdStrm }: any) => artistIdStrm)
  );
  const [filter, setFilter] = useState<any>(defaultFilter);
  const [rankingIndex, setRankingIndex] = useState<ArtistsRankingIndex>(
    savedIndex ? savedIndex : ArtistsRankingIndex.Performance
  );
  const [highlightedArtist, setHighlightedArtist] = useState<any>({});
  const [genreSpotifyOptions, setGenreSpotifyOptions] = useState<any[]>([]);
  const [form] = Form.useForm();

  const { genres, genreOptions, genresSpotify } = useSelector(
    (state: any) => state.genres
  );
  const { countries, countryOptions } = useSelector(
    (state: any) => state.countries
  );

  const loggedUser = useSelector((state: any) => state.users.loggedUser);
  const { artistsRanking, loading } = useSelector(
    (state: any) => state.rankings
  );

  const admin = loggedUser?.typeAccess === UserTypeEnum.AdminStrm;
  const dispatch = useDispatch();
  const wrappGetI18nGenre = (genre: string) =>
    getI18nGenre(genre, i18n, genres);

  const getI18nCoutry = (country: string) => {
    if (country) {
      const countryI18n = countries.find((gen: any) => gen.code === country);
      if (countryI18n)
        return countryI18n[`description${getCapitalizedLang(i18n)}`];
      return country;
    }
    return "";
  };

  const daysOptions = [
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 1 }),
      value: 365,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 2 }),
      value: 730,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 3 }),
      value: 1095,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 4 }),
      value: 1460,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 5 }),
      value: 1825,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 10 }),
      value: 3650,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 15 }),
      value: 5475,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 20 }),
      value: 7300,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 30 }),
      value: 10950,
    },
  ];

  const daysWithoutReleaseOptions = [
    { label: 1, value: 365 },
    { label: 2, value: 730 },
    { label: 3, value: 1095 },
    { label: 4, value: 1460 },
    { label: 5, value: 1825 },
    { label: 10, value: 3650 },
    { label: 15, value: 5475 },
    { label: 20, value: 7300 },
    { label: 30, value: 10950 },
  ];

  const colunas: ColumnsType<any> = [
    {
      dataIndex: "position",
      width: "5%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
    {
      dataIndex: "positionDif",
      align: "center",
      render: (value, record: any) => {
        if (!record.position) return "N/A";
        return (
          <span
            className={`position-difference ${
              value === null
                ? "blue"
                : value > 0
                ? "green"
                : value < 0
                ? "red"
                : ""
            }`}
          >
            {value === null ? (
              "New"
            ) : value === 0 ? (
              <FontAwesomeIcon icon={faStop} />
            ) : value > 0 ? (
              <>
                <FontAwesomeIcon icon={faArrowUp} />
                {` ${value}`}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faArrowDown} />
                {` ${Math.abs(value)}`}
              </>
            )}
          </span>
        );
      },
      width: "5%",
    },
    {
      title: t("pages.ArtistsRanking.artistName"),
      dataIndex: "artistName",
      width: "15%",
    },
    {
      title: t("pages.ArtistsRanking.genres"),
      dataIndex: "genresStrm",
      width: "15%",
      render: (value) =>
        value.map((genre: any) => (
          <Tag key={Math.random()}>{wrappGetI18nGenre(genre)}</Tag>
        )),
    },
    {
      title: t("pages.ArtistsRanking.country"),
      dataIndex: "country",
      align: "center",
      width: "10%",
      render: (value: string) => <>{getI18nCoutry(value)}</>,
    },
    {
      title: t("pages.ArtistsRanking.stage"),
      dataIndex: "careerStage",
      align: "center",
      width: "10%",
    },
    {
      title: t("pages.ArtistsRanking.development"),
      dataIndex: "developmentIndex",
      render: (value) => <>{(value || value === 0) && value.toPrecision(3)}</>,
      align: "center",
      width: "10%",
    },
    {
      title: t("pages.ArtistsRanking.performance"),
      dataIndex: "performanceIndex",
      render: (value) => <>{(value || value === 0) && value.toPrecision(3)}</>,
      align: "center",
      width: "10%",
    },
    {
      title: t("pages.ArtistsRanking.popularityIndex"),
      dataIndex: "popularityIndex",
      align: "center",
      width: "10%",
    },
  ];

  if (admin) {
    colunas.push({
      key: "acao",
      width: "5%",
      align: "right",
      render: (_, record) => (
        <a
          href={`/artist-profile/${record.artistIdStrm}`}
          style={{ color: "white" }}
          target="_blank"
          rel="noreferrer"
          onClick={(evt) => evt.stopPropagation()}
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} />
        </a>
      ),
    });
  }

  const onSelectSpotifyGenre = (value: any) => {
    if (value === "all") {
      form.setFieldsValue({ genreSpotify: genresSpotify });
    }
  };

  const getHighlightedArtists = useCallback(async () => {
    setLoadingHighlightedArtists(true);

    const combinedFilter = {
      ...filter,
      artistHighlighteds: selectedArtists,
      artistSortMode: rankingIndex,
    };

    if (
      selectedArtists.length === 0 &&
      highlightedArtist[`${rankingIndex}`]?.artists?.length
    )
      setHighlightedArtist({});
    if (
      selectedArtists.length > 0 &&
      highlightedArtist[`${rankingIndex}`]?.artists?.length !==
        selectedArtists.length
    ) {
      try {
        const result = await fetchHighlightedArtists(combinedFilter);
        setHighlightedArtist({
          ...highlightedArtist,
          [`${rankingIndex}`]: { ...result, ...result.highlighted },
        });
      } catch (error) {
        console.error("Erro ao buscar artistas destacados:", error);
      }
    }

    setLoadingHighlightedArtists(false);
  }, [filter, highlightedArtist, selectedArtists, rankingIndex]);

  const onFinishFilter = () => {
    const {
      country,
      daysSinceFirstRelease,
      daysWithoutRelease,
      genre,
      genreSpotify,
      releasesLastYear,
      stage,
    } = form.getFieldsValue(true);
    const [stageInit, stageEnd] = stage;
    const saveFilter = {
      ...defaultFilter,
      country: country?.length === 0 ? undefined : country,
      daysSinceFirstRelease,
      daysWithoutRelease,
      genre,
      genreSpotify,
      releasesLastYear,
      stageInit,
      stageEnd,
    };
    dispatch(clearArtistRanking());
    setHighlightedArtist({});
    localStorage.setItem(
      RADAR_ARTIST_RANKING_FILTER,
      JSON.stringify(saveFilter)
    );
    setFilter(saveFilter);
  };

  const getArtists = async (artistNameTerm: string) => {
    if (artistNameTerm.replace(/[/\\#,+()$~%.'":*?<>{}]/g, "")) {
      setLoadingArtist(true);
      setArtistsOptions([]);
      const response = await fetchArtistByName({ artistNameTerm });
      setSearchedArtists(response);
      setLoadingArtist(false);
    }
  };

  const onDeleteClick = () => {
    dispatch(clearArtistRanking());
    form.setFieldsValue(defaults.defaultForm);
    setFilter(defaults.defaultFilter);
    localStorage.setItem(
      RADAR_ARTIST_RANKING_FILTER,
      JSON.stringify(defaults.defaultFilter)
    );
  };

  const onChangeArtist = async (value: any) => {
    if (value?.length > 3) {
      value.pop();
    }
    const highlightedArtists = localStorage.getItem(
      RADAR_ARTIST_RANKING_HIGHLIGHTED_ARTISTS
    );
    const artist: any = searchedArtists.find((opt: any) =>
      value.includes(opt.artistIdStrm)
    );

    if (!artist) return;

    if (highlightedArtists) {
      const parsed = JSON.parse(highlightedArtists);
      const uniqArray = uniqBy([...parsed, artist], "artistIdStrm").filter(
        (artist: any) => artist && value.includes(artist.artistIdStrm)
      );
      localStorage.setItem(
        RADAR_ARTIST_RANKING_HIGHLIGHTED_ARTISTS,
        JSON.stringify(uniqArray)
      );
    } else {
      localStorage.setItem(
        RADAR_ARTIST_RANKING_HIGHLIGHTED_ARTISTS,
        JSON.stringify([artist])
      );
    }

    setSelectedArtists(value);
  };

  const downloadRankingReportCSV = async () => {
    setLoadingLocal(true);
    const combinedFilter = {
      ...filter,
      artistSortMode: rankingIndex,
    };
    const data: any = await exportArtistRankingCSV(combinedFilter);
    downloadFile(data, "artist-ranking.csv", FileType.Csv);
    setLoadingLocal(false);
    setEnableExport(false);
  };

  const downloadRankingReportExcel = async () => {
    setLoadingLocal(true);
    const combinedFilter = {
      ...filter,
      artistSortMode: rankingIndex,
    };
    const data: any = await exportArtistRankingExcel(combinedFilter);
    downloadFile(data, "artist-ranking.xlsx", FileType.Xlsx);
    setLoadingLocal(false);
    setEnableExport(false);
  };

  const onChangeIndex = (evt: any) => {
    const saveRankingIndex = evt.target.value;
    localStorage.setItem("RADAR_RANKING_INDEX", saveRankingIndex);
    setRankingIndex(saveRankingIndex);
  };

  const onRowClick = (record: any) => {
    history.push("/artist-profile", {
      filter: filter,
      artist: record,
    });
  };

  const formatDate = (date: string) =>
    date && moment(date).format("DD/MM/YYYY");

  const onSelectGenre = (value: any) => {
    if (value === "all") {
      const filteredGenres = genreOptions.map((item: any) => item.value);
      form.setFieldsValue({ genre: filteredGenres });
    }
  };

  const handlePageChange = (page: number, pageSize: number) => {
    const offset = (page - 1) * pageSize;

    if (filter.offset !== offset || filter.limit !== pageSize) {
      const newFilter = {
        ...filter,
        offset: offset,
        limit: pageSize,
      };

      localStorage.setItem(
        RADAR_ARTIST_RANKING_FILTER,
        JSON.stringify(newFilter)
      );
      setFilter(newFilter);
    }
  };

  useEffect(() => {
    setArtistsOptions(
      searchedArtists.map(
        ({ artistIdStrm, artistName, imagesSpotify }: any) => ({
          id: artistIdStrm,
          name: artistName,
          image: (imagesSpotify && imagesSpotify[2]?.url) || "",
        })
      )
    );
  }, [searchedArtists]);

  useEffect(() => {
    dispatch(
      getArtistRanking({
        filter,
        rankingIndex,
      })
    );
  }, [rankingIndex, filter, dispatch]);

  useEffect(() => {
    getHighlightedArtists();
  }, [selectedArtists, getHighlightedArtists]);

  useEffect(() => {
    setGenreSpotifyOptions(
      genresSpotify
        .map((genre: any) => ({
          label: genre,
          value: genre,
        }))
        .sort((a: any, b: any) =>
          a.label > b.label ? 1 : a.label < b.label ? -1 : 0
        )
    );
  }, [genresSpotify]);

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Ranking.S4L_RANKING_ARTISTS,
    });
  }, []);

  return (
    <div className="radar-home" style={{ color: "white" }}>
      <Row style={{ marginTop: "16px" }}>
        <Col style={{ textAlign: "center" }}>
          <h1 className="ranking-title">{t("pages.ArtistsRanking.title")}</h1>
        </Col>
      </Row>
      <Row style={{ marginTop: "16px" }}>
        <Col style={{ textAlign: "center" }}>
          <RadioButtonRankingOptions value="artist" />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} className="conteudo-home">
          <Form
            form={form}
            initialValues={defaultForm}
            onFinish={onFinishFilter}
            className="filtro"
            layout="vertical"
          >
            <Card>
              <Row gutter={24}>
                <Col xs={5}>
                  <Form.Item
                    name="genre"
                    label={t("pages.ArtistsRanking.genre")}
                  >
                    <FieldSelect
                      mode="multiple"
                      showSelectAll
                      dataList={genreOptions}
                      onSelect={onSelectGenre}
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="genreSpotify"
                    label={t("pages.ArtistsRanking.genreSpotify")}
                  >
                    <FieldSelect
                      mode="multiple"
                      showSelectAll
                      onSelect={onSelectSpotifyGenre}
                      dataList={genreSpotifyOptions}
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="country"
                    label={t("pages.ArtistsRanking.country")}
                  >
                    <FieldSelect
                      mode="multiple"
                      showSelectAll={false}
                      optionFilterProp="children"
                      filterOption={(input: string, option: any) =>
                        option?.children
                          ?.toUpperCase()
                          ?.includes(input.toUpperCase())
                      }
                      dataList={countryOptions}
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="stage"
                    label={t("pages.ArtistsRanking.stage")}
                  >
                    <Slider
                      min={1}
                      max={10}
                      // tooltipVisible
                      marks={{
                        1: {
                          style: {
                            color: "#fff",
                          },
                          label: <strong>1</strong>,
                        },
                        10: {
                          style: {
                            color: "#fff",
                          },
                          label: <strong>10</strong>,
                        },
                      }}
                      range
                      tooltipPlacement="bottom"
                    />
                  </Form.Item>
                </Col>
                <Col xs={4}>
                  <div className="filter-button ">
                    <span className="delete" onClick={onDeleteClick}>
                      {t("pages.ArtistsRanking.delete")}
                    </span>
                    <button
                      className="apply"
                      disabled={loading || loadingLocal}
                    >
                      {t("pages.ArtistsRanking.filter")}
                    </button>
                  </div>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="daysSinceFirstRelease"
                    label={t("pages.ArtistsRanking.daysSinceFirstRelease")}
                  >
                    <Select
                      options={daysOptions}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      showArrow={false}
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="daysWithoutRelease"
                    label={t("pages.ArtistsRanking.daysWithoutRelease")}
                  >
                    <Select
                      options={daysWithoutReleaseOptions}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      showArrow={false}
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="releasesLastYear"
                    label={t("pages.ArtistsRanking.releasesInThePastYear")}
                  >
                    <InputNumber
                      min={0}
                      placeholder={t("pages.ArtistsRanking.typeHere")}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Form>
          <Row style={{ marginTop: 16, marginBottom: 32 }}>
            <Col style={{ textAlign: "center" }}>
              <Radio.Group value="ranking">
                <Radio.Button
                  value="ranking"
                  onClick={() => history.push("/artist-ranking")}
                >
                  {t("pages.ArtistsRanking.title")}
                </Radio.Button>
                <Radio.Button
                  value="network"
                  onClick={() => history.push("/artist-network")}
                >
                  {t("pages.ArtistsRanking.artistNetwork")}
                </Radio.Button>
              </Radio.Group>
            </Col>
          </Row>
          <Row style={{ marginBottom: 16 }}>
            <Col xs={8}>
              <span
                style={{ display: "block", marginTop: -30, marginBottom: 8 }}
              >
                {t("pages.ArtistsRanking.searchArtist")}
              </span>
              <Select
                defaultValue={defaultArtists.map(
                  ({ artistIdStrm }: any) => artistIdStrm
                )}
                filterOption={false}
                tagRender={tagRender}
                placeholder={t("pages.ArtistsRanking.typeHere")}
                onSearch={debounce(getArtists, 1000)}
                onChange={onChangeArtist}
                style={{ width: "80%" }}
                mode="multiple"
                notFoundContent={
                  <Loader.NotFoundLoader loading={loadingArtist} />
                }
              >
                {artistsOptions.map((artist: any) => (
                  <Select.Option
                    value={artist.id}
                    key={artist.id}
                    label={artist.name}
                  >
                    {artist.image && (
                      <Avatar
                        style={{ marginRight: "6px" }}
                        src={artist.image}
                      />
                    )}
                    {artist.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col
              xs={16}
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Radio.Group onChange={onChangeIndex} value={rankingIndex}>
                <Radio.Button value={ArtistsRankingIndex.Performance}>
                  <Tooltip
                    title={t("pages.ArtistsRanking.performanceExplaination")}
                    overlayStyle={{ width: 500 }}
                  >
                    {t("pages.ArtistsRanking.performance")}
                  </Tooltip>
                </Radio.Button>
                <Radio.Button value={ArtistsRankingIndex.DeltaSpotify}>
                  <Tooltip
                    overlayStyle={{ width: 500 }}
                    title={t("pages.ArtistsRanking.deltaSpotifyExplaination")}
                  >
                    {t("pages.ArtistsRanking.spotifyDelta")}
                  </Tooltip>
                </Radio.Button>
                <Radio.Button
                  value={ArtistsRankingIndex.PerformanceDevelopment}
                >
                  <Tooltip
                    overlayStyle={{ width: 500 }}
                    title={t(
                      "pages.ArtistsRanking.performanceDevelopmentExplaination"
                    )}
                  >
                    {t("pages.ArtistsRanking.performanceDevelopment")}
                  </Tooltip>
                </Radio.Button>
                <Radio.Button value={ArtistsRankingIndex.GrowthSpeed}>
                  <Tooltip
                    overlayStyle={{ width: 500 }}
                    title={t("pages.ArtistsRanking.growthSpeedExplaination")}
                  >
                    {t("pages.ArtistsRanking.growthSpeed")}
                  </Tooltip>
                </Radio.Button>
                <Radio.Button value={ArtistsRankingIndex.Volatility}>
                  <Tooltip
                    overlayStyle={{ maxWidth: 550 }}
                    title={t("pages.ArtistsRanking.volatilityExplaination")}
                  >
                    {t("pages.ArtistsRanking.volatility")}
                  </Tooltip>
                </Radio.Button>
              </Radio.Group>
            </Col>
          </Row>
          <Row justify="end" style={{ gap: 12 }}>
            {enableExport && (
              <div className="container-botoes-exportar">
                <button
                  className="button-export"
                  onClick={() => downloadRankingReportCSV()}
                >
                  CSV
                </button>
                <button
                  className="button-export"
                  onClick={() => downloadRankingReportExcel()}
                >
                  Excel
                </button>
              </div>
            )}
            <button
              className="botao-azul button-export"
              onClick={() => setEnableExport(!enableExport)}
            >
              {t("pages.ArtistProfile.export")}
              <ExportIcon className="download-icon" />
            </button>
          </Row>
          <Row style={{ marginBottom: 30 }}>
            <Col xs={12}>
              <Typography.Title
                level={4}
                style={{ color: "white", textAlign: "left" }}
              >
                {t("pages.ArtistsRanking.results", {
                  total: ptBRFormat(
                    artistsRanking[`${rankingIndex}`]?.total || 0
                  ),
                })}
              </Typography.Title>
            </Col>
            <Col xs={12}>
              <Typography.Title
                level={5}
                style={{ color: "white", textAlign: "right" }}
              >
                {t("pages.ArtistsRanking.lastUpdate")}
                <b>
                  {`${
                    formatDate(
                      artistsRanking[`${rankingIndex}`]?.rankingDate
                    ) || ""
                  } `}
                </b>
              </Typography.Title>
            </Col>
          </Row>
          <Row>
            <Col xs={24}>
              <Table
                columns={colunas}
                dataSource={artistsRanking[`${rankingIndex}`]?.artists || []}
                rowKey="artistIdStrm"
                loading={loading || loadingLocal}
                onRow={(data) => ({ onClick: () => onRowClick(data) })}
                rowClassName="clickable-row"
                pagination={false}
              />
              <Col
                xs={24}
                hidden={
                  !(highlightedArtist[`${rankingIndex}`]?.artists?.length > 0)
                }
              >
                <Table
                  columns={colunas}
                  loading={loadingHighlightedArtists}
                  dataSource={
                    highlightedArtist[`${rankingIndex}`]?.artists || []
                  }
                  onRow={(data) => ({ onClick: () => onRowClick(data) })}
                  rowClassName="highlight-artists"
                  className="highlight-table clickable-row"
                  showHeader={false}
                  pagination={false}
                />
              </Col>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Pagination
                  style={{ alignSelf: "flex-end", margin: "16px" }}
                  hideOnSinglePage={true}
                  onChange={handlePageChange}
                  onShowSizeChange={handlePageChange}
                  current={filter.offset / filter.limit + 1}
                  pageSize={filter.limit}
                  total={artistsRanking[`${rankingIndex}`]?.total}
                />
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

function tagRender(props: any) {
  const { label, closable, onClose } = props;
  const onPreventMouseDown = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Tag
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{ marginRight: 3, marginBottom: 3 }}
    >
      {label}
    </Tag>
  );
}

export default ArtistsRanking;
